import { useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import { MMB_DEPTH_SYNC_URL } from "./constants";
import { getHeaders } from "./utils";

const SYMBOLS = ["SPY", "QQQ"];
const headers = () => ({ ...getHeaders(), "Content-Type": "application/json" });

function parsePrices(text, symbol) {
  const parts = text.trim().split(/[\s,;]+/).filter(Boolean);
  if (!parts.length) throw new Error(`Enter at least one MMB price for ${symbol}.`);
  if (parts.length > 30) throw new Error(`${symbol} supports up to 30 prices.`);
  const prices = parts.map(Number);
  if (prices.some(price => !Number.isFinite(price) || price <= 0)) {
    throw new Error(`${symbol} prices must be positive numbers separated by commas or new lines.`);
  }
  if (new Set(prices).size !== prices.length) throw new Error(`${symbol} has duplicate prices.`);
  return prices;
}

function describe(error) {
  return error.response?.data?.error || error.message || "Unknown error";
}

export default function MmbDepthModal({ show, onHide }) {
  const [mode, setMode] = useState("discord");
  const [selected, setSelected] = useState({ SPY: true, QQQ: true });
  const [inputs, setInputs] = useState({ SPY: "", QQQ: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const close = () => { if (!busy) onHide(); };
  const sync = async () => {
    setError("");
    setResult(null);
    const symbols = SYMBOLS.filter(symbol => selected[symbol]);
    if (!symbols.length) { setError("Select SPY, QQQ, or both."); return; }
    let levels;
    try {
      if (mode === "manual") {
        levels = Object.fromEntries(symbols.map(symbol => [symbol, parsePrices(inputs[symbol], symbol)]));
      }
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    setBusy(true);
    try {
      const response = await axios.post(MMB_DEPTH_SYNC_URL, { mode, symbols, levels }, {
        headers: headers(), withCredentials: true,
      });
      const deadline = Date.now() + 12 * 60 * 1000;
      let job = response.data;
      while (job.status === "running" && Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const status = await axios.get(`${MMB_DEPTH_SYNC_URL}/${job.id}`, {
          headers: getHeaders(), withCredentials: true, timeout: 20000,
        });
        job = status.data;
      }
      if (job.status === "failed") throw new Error(job.error || "MMB depth sync failed");
      if (job.status !== "complete") throw new Error("Sync is still running. Reopen this window to try again later.");
      setResult(job.levels);
    } catch (requestError) {
      setError(describe(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={close} centered size="lg" data-bs-theme="dark">
      <Modal.Header closeButton={!busy} className="bg-dark text-light border-secondary">
        <Modal.Title>Sync MMB Depth</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light">
        <Form.Group className="mb-3">
          <Form.Label>Source</Form.Label>
          <Form.Check type="radio" name="mmb-source" id="mmb-discord" label="Latest MMB from Discord"
            checked={mode === "discord"} disabled={busy} onChange={() => { setMode("discord"); setError(""); setResult(null); }} />
          <Form.Check type="radio" name="mmb-source" id="mmb-manual" label="Enter MMB prices manually"
            checked={mode === "manual"} disabled={busy} onChange={() => { setMode("manual"); setError(""); setResult(null); }} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Symbols to sync</Form.Label>
          <div className="d-flex gap-4">
            {SYMBOLS.map(symbol => (
              <Form.Check key={symbol} type="checkbox" id={`mmb-${symbol}`} label={symbol}
                checked={selected[symbol]} disabled={busy}
                onChange={event => { setSelected({ ...selected, [symbol]: event.target.checked }); setResult(null); }} />
            ))}
          </div>
        </Form.Group>
        {mode === "manual" && SYMBOLS.filter(symbol => selected[symbol]).map(symbol => (
          <Form.Group key={symbol} className="mb-3" controlId={`mmb-prices-${symbol}`}>
            <Form.Label>{symbol} MMB prices</Form.Label>
            <Form.Control as="textarea" rows={3} placeholder="One price per line, or separate with commas"
              value={inputs[symbol]} disabled={busy} className="bg-dark text-light border-secondary"
              onChange={event => setInputs({ ...inputs, [symbol]: event.target.value })} />
          </Form.Group>
        ))}
        {mode === "discord" && <p className="text-secondary mb-3">Uses the latest chart posted for each selected symbol, then extracts its depth levels.</p>}
        {error && <Alert variant="danger" className="mb-0">{error}</Alert>}
        {result && <Alert variant="success" className="mb-0">
          MMB depth synced. {Object.entries(result).map(([symbol, prices]) =>
            <div key={symbol}>{symbol}: {prices.length ? prices.join(", ") : "no qualifying levels; old lines cleared"}</div>
          )}
        </Alert>}
      </Modal.Body>
      <Modal.Footer className="bg-dark border-secondary">
        <Button variant="secondary" onClick={close} disabled={busy}>Close</Button>
        <Button variant="primary" onClick={sync} disabled={busy || !SYMBOLS.some(symbol => selected[symbol])}>
          {busy ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Syncing…</> : "Sync"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
